import "../assets/css/mini.css";

import React, { useEffect, useState, useCallback, useMemo } from "react";

import { Spin, Modal, Empty } from "antd";

import { LoadingOutlined } from "@ant-design/icons";
import { DateTime } from "luxon";

import { Download, Plus, Users } from "lucide-react";
import FooterDisplay from "../components/footer";
import { useSelector } from "react-redux";
import NumberFormat from "react-number-format";
import WalletHistoryDetailPage from "../components/profile/history/wallet";
import TopNav from "../components/top-nav";
import { useLocation } from "react-router-dom";
import WalletCard from "../components/WalletCard";
import PinActivationWatcher from "../components/PinActivationWatcher";
import FundWalletModalContainer from "../components/transactions/FundWalletModal";
import TransferModal from "../components/transactions/TransferModal";
import BillPaymentsModal from "../components/transactions/BillPaymentsModal";
import RestrictedFeatureModal from "../components/modals/RestrictedFeatureModal";
import useWalletData from "../hooks/useWalletData";
import useWalletHistory from "../hooks/useWalletHistory";
import {
  _get_fourward_account,
  _get_user_wallet,
  _get_user_wallet_transactions,
} from "../common/axios_services";
import HelpSupportButton from "../components/HelpSupportButton";
import {
  AdminCollapseList,
  buildAdminCollapseItem,
} from "../components/admin/AdminCollapseItem";

const WalletPage = () => {
  const majorSpinnerIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const [displayMode, setDisplayMode] = useState("content");
  const [historyData, setHistoryData] = useState({});
  const [userLogInStatus, setUserLogInStatus] = useState(false);
  const [checkingUserLogInStatus, setCheckingUserLogInStatus] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showCashbackModal, setShowCashbackModal] = useState(false);
  const [billPaymentsVisible, setBillPaymentsVisible] = useState(false);
  const [restrictionModalVisible, setRestrictionModalVisible] = useState(false);
  const [currentHistDisp, setCurrentHistDisp] = useState("wallet");
  const [fourwardHistory, setFourwardHistory] = useState([]);
  const [fourwardAccount, setFourwardAccount] = useState(null);
  const location = useLocation();
  const shouldOpenFundWallet = Boolean(
    location.state?.openFundWallet ||
    location.state?.openAddFunds ||
    new URLSearchParams(location.search).get("action") === "fund"
  );

  useEffect(() => {
    if (location.state?.openFundWallet || location.state?.openAddFunds) {
      const timer = setTimeout(() => {
        try {
          window.history.replaceState(
            { ...(window.history.state || {}), usr: null },
            document.title
          );
        } catch (e) {}
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const [userCountdownValue, setUserCountdownValue] = useState({
    remaining_days: 0,
    remaining_orders: 0,
  });

  const {
    majorPageSpinner,
    walletBalance,
    walletData,
    userData,
    userTransferData,
    displayUserWallet,
    userloginPinSet,
    setUserloginPinSet,
    fetchUserWalletBalance,
    fetchUser,
    fetchUserWallet,
    getWalletBalance,
    handleTitleCheckChange,
  } = useWalletData();

  useEffect(() => {
    if (userData?.cashback_campaign) {
      setUserCountdownValue(userData.cashback_campaign);
    }
  }, [userData]);

  const {
    loyaltyHis,
    fetchingLoyalty,
    pageLoy,
    pageSizeLoy,
    totalLoy,
    setPageLoy,
    setPageSizeLoy,
    fetchUserWalletPayments,
  } = useWalletHistory();

  const fetchFourwardHistory = useCallback(async () => {
    try {
      const response = await _get_fourward_account();
      const account = response?.data?.data || response?.data || {};
      setFourwardAccount(account?.account || null);
      setFourwardHistory(
        Array.isArray(account?.transactions) ? account.transactions : [],
      );
    } catch (error) {
      setFourwardAccount(null);
      setFourwardHistory([]);
    }
  }, []);

  useEffect(() => {
    fetchUser();
    fetchUserWalletBalance();
    fetchUserWalletPayments();
    fetchFourwardHistory();
    setUserLogInStatus(true);
    fetchUserWallet();
    getWalletBalance();
    setCheckingUserLogInStatus(false);
  }, []);

  useEffect(() => {
    fetchUserWalletPayments();
    setExpandedRow(null);
  }, [pageLoy]);

  const setDisplayModeToContent = () => {
    setDisplayMode("content");
  };

  const handleCloseCashbackModal = useCallback(() => {
    setShowCashbackModal(false);
  }, []);

  const cashbackModalFooter = useMemo(
    () => [
      <button
        key="okay"
        className="bg-gray-900 text-white px-6 py-2 rounded-lg"
        onClick={handleCloseCashbackModal}
      >
        Ok
      </button>,
    ],
    [handleCloseCashbackModal],
  );

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handlePayBillsClick = () => {
    if (userData?.tier === "tier_1") {
      setRestrictionModalVisible(true);
    } else {
      setBillPaymentsVisible(true);
    }
  };

  return (
    <React.Fragment>
      {displayMode === "content" ? (
        <div className="wallet-content wise9ja-content">
          <CashbackModal
            visible={showCashbackModal}
            onCancel={handleCloseCashbackModal}
            footer={cashbackModalFooter}
            userCountdownValue={userCountdownValue}
          />

          <BillPaymentsModal
            visible={billPaymentsVisible}
            onCancel={() => setBillPaymentsVisible(false)}
            onPaymentSuccess={() => {
              fetchUserWalletBalance();
              fetchUserWalletPayments();
              fetchUserWallet();
              getWalletBalance();
            }}
            walletBalance={walletBalance?.balance || 0}
          />

          <RestrictedFeatureModal
            visible={restrictionModalVisible}
            onCancel={() => setRestrictionModalVisible(false)}
            featureName="Pay Bill"
          />

          {checkingUserLogInStatus ? (
            <div>
              <TopNav title="Your Wallet" />
              <div className="center-content">
                <div className="center-content-text">
                  <Spin indicator={majorSpinnerIcon} />
                </div>
              </div>
            </div>
          ) : userLogInStatus ? (
            <div>
              <Spin indicator={majorSpinnerIcon} spinning={majorPageSpinner}>
                <div className="homepage wise9ja-disp">
                  <div className="homepage-history-header">
                    <TopNav title="Your Wallet" />
                    <div className="contain">
                      <div className=" mt-9 mb-4">
                        <WalletCard
                          tierType={userData?.tier?.split("_")[1]}
                          displayTitleCheck={displayUserWallet}
                          userTransferData={userTransferData}
                          majorPageSpinner={majorPageSpinner}
                          walletBalance={walletBalance}
                          fourwardAccount={fourwardAccount}
                          walletData={walletData}
                          userData={userData}
                          onPayBillsClick={handlePayBillsClick}
                          handleTitleCheckChange={handleTitleCheckChange}
                        />
                        <PinActivationWatcher />
                      </div>
                      <WalletActions
                        walletBalance={walletBalance}
                        initialOpenAdd={shouldOpenFundWallet}
                        onPaymentSuccess={() => {
                          fetchUserWalletBalance();
                          fetchUserWalletPayments();
                          fetchUserWallet();
                          getWalletBalance();
                        }}
                        onPayBillsClick={handlePayBillsClick}
                      />
                    </div>
                  </div>
                  <div className="content-display-content">
                    <div className="contai">
                      <div className="wise9ja-action-bar"></div>
                      <div className="mt_3 history-data-detail">
                        <div className="contain">
                          <HistoryTabs
                            currentHistDisp={currentHistDisp}
                            onTabChange={(tab) => {
                              setCurrentHistDisp(tab);
                              setExpandedRow(null);
                              if (tab === "fourward") fetchFourwardHistory();
                            }}
                          />

                          <div className="contai">
                            {currentHistDisp === "wallet" && (
                              <HistoryList
                                type="wallet"
                                data={loyaltyHis}
                                expandedRow={expandedRow}
                                onRowClick={handleRowClick}
                                currentPage={pageLoy}
                                totalItems={totalLoy}
                                pageSize={pageSizeLoy}
                                onPageChange={setPageLoy}
                              />
                            )}

                            {currentHistDisp === "fourward" && (
                              <FourwardHistoryList data={fourwardHistory} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <FooterDisplay />
                </div>
              </Spin>
            </div>
          ) : (
            ""
          )}
          <FooterDisplay />
        </div>
      ) : (
        <WalletHistoryDetailPage
          historyData={historyData}
          displayModeControl={setDisplayModeToContent}
        />
      )}
      <HelpSupportButton page="WALLET" />
    </React.Fragment>
  );
};

export default WalletPage;

const CashbackModal = ({ visible, onCancel, footer, userCountdownValue }) => (
  <Modal
    title=""
    open={visible}
    onCancel={onCancel}
    footer={footer}
    centered
    className="how-it-works"
  >
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-5xl">
          Congratulations, &nbsp;
          <span className="text-[#44843F]">NGN 16,500</span> is ready...
        </h3>
        <p className="my-2 leading-tight">
          <strong>NGN 825</strong> is in your wallet to buy food{" "}
          <strong>TODAY</strong>.
          <br />
          <br />
          We will credit your wallet with <strong>NGN 825</strong> after every
          successful order for{" "}
          <strong>{userCountdownValue?.remaining_orders}</strong>
          {userCountdownValue?.remaining_orders === 1 ? " order" : " orders"}
          .
          <br />
          <br />
          This offer expires in{" "}
          <strong>{userCountdownValue?.remaining_days}</strong>{" "}
          {userCountdownValue?.remaining_days === 1 ? "day" : "days"}
        </p>
      </div>
    </div>
  </Modal>
);

const WalletActions = ({
  walletBalance,
  onPaymentSuccess,
  onPayBillsClick,
  initialOpenAdd = false,
}) => {
  const [addVisible, setAddVisible] = useState(initialOpenAdd);

  useEffect(() => {
    if (initialOpenAdd) {
      setAddVisible(true);
    }
  }, [initialOpenAdd]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 justify-between gap-4">
      <div className="flex flex-col items-center justify-center border rounded-lg p-4">
        <button
          type="button"
          onClick={() => setAddVisible(true)}
          className="flex flex-col items-center"
        >
          <div className="bg-green-50 p-2 rounded-lg mb-2">
            <Plus className="text-green-600 mx-auto" size={24} />
          </div>
          <p className="font-bold">Add Funds</p>
        </button>
        <Modal
          open={addVisible}
          onCancel={() => setAddVisible(false)}
          footer={null}
          centered
          title="Add to your wallet"
        >
          <div className="space-y-3">
            <FundWalletModalContainer onBalanceUpdate={onPaymentSuccess}>
              <div className="w-full flex items-center gap-3 border rounded-xl p-4">
                <Download className="text-blue-600" size={24} />
                <span className="font-bold">Fund Wallet</span>
              </div>
            </FundWalletModalContainer>
            <a
              href="/fourward"
              className="w-full flex items-center gap-3 border rounded-xl p-4 text-left text-gray-900"
            >
              <Plus className="text-green-600" size={24} />
              <span className="font-bold">Open 4Ward</span>
            </a>
            {/* <a href="/group-account" className="w-full flex items-center gap-3 border rounded-xl p-4 text-left text-gray-900">
              <Users className="text-green-600" size={24} />
              <span className="font-bold">Create Group Account</span>
            </a> */}
          </div>
        </Modal>
      </div>
      {/* <div className="flex flex-col items-center justify-center border rounded-lg p-4">
      <FundWalletModalContainer onBalanceUpdate={onPaymentSuccess}>
        <div className="bg-blue-50 p-2 rounded-lg mb-2">
          <Download className="text-blue-600 mx-auto text-center" size={24} />
        </div>
        <p className="font-bold">Fund Wallet</p>
      </FundWalletModalContainer>
    </div> */}
      <div className="flex flex-col items-center border rounded-lg p-4">
        <TransferModal onComplete={onPaymentSuccess} />
      </div>
    </div>
  );
};

const HistoryTabs = ({ currentHistDisp, onTabChange }) => (
  <div className="top-history-header">
    <div
      onClick={() => onTabChange("wallet")}
      className={`history-box ${currentHistDisp === "wallet" ? "active-history" : ""}`}
    >
      <h3 className="tile-header">Wallet History</h3>
    </div>
    <div
      onClick={() => onTabChange("fourward")}
      className={`history-box ${currentHistDisp === "fourward" ? "active-history" : ""}`}
    >
      <h3 className="tile-header">4Ward History</h3>
    </div>
  </div>
);

const HistoryList = ({
  type,
  data,
  expandedRow,
  onRowClick,
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
}) => (
  <div>
    {data && data.length > 0 ? (
      data.map((history, index) => (
        <div className="history-item-container" key={`verified-${index}`}>
          <div
            className="history-item-summary clickable"
            onClick={() => onRowClick(index)}
          >
            <ul>
              <li>
                <p style={{ display: "inline" }}>
                  <span className="small_currency">NGN</span>
                  <NumberFormat
                    thousandSeparator={true}
                    prefix=""
                    className="numeric"
                    displayType="text"
                    value={(+history.total_amount).toFixed(2)}
                  />{" "}
                  {history.description ||
                    history.payment_type ||
                    history.service}{" "}
                  on{" "}
                  {DateTime.fromISO(
                    history.timestamp.split("T")[0],
                  ).toLocaleString(DateTime.DATE_HUGE)}
                </p>
              </li>
            </ul>
            {expandedRow === index ? " ▲" : " ▼"}
          </div>
          {expandedRow === index && (
            <div className="expanded-details">
              <Item
                title="Date"
                value={DateTime.fromISO(history.timestamp).toLocaleString(
                  DateTime.DATE_HUGE,
                )}
              />
              {type === "wallet" && (
                <>
                  <Item
                    title="Charges"
                    value={
                      <span>
                        <span className="currency">NGN</span>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix=""
                          className="numeric"
                          displayType="text"
                          value={Number(history.charges ?? 0.0).toFixed(2)}
                        />
                      </span>
                    }
                  />
                  <Item
                    title="Amount"
                    value={
                      <span>
                        <span className="currency">NGN</span>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix=""
                          className="numeric"
                          displayType="text"
                          value={Number(history.amount).toFixed(2)}
                        />
                      </span>
                    }
                  />
                  <Item title="Status" value={history?.status} />
                  <Item title="Description" value={history?.description} />
                  <Item title="Payment Type" value={history?.payment_type} />
                </>
              )}
              {type === "bonus" && (
                <>
                  <Item title="Service" value={history.service} />

                  <Item
                    title="Amount"
                    value={
                      <span>
                        <span className="currency">NGN</span>
                        <NumberFormat
                          thousandSeparator={true}
                          displayType="text"
                          value={Math.trunc(+history.new_voucher_bal).toFixed(
                            2,
                          )}
                        />
                      </span>
                    }
                  />
                </>
              )}
              {history.new_wallet_balance && (
                <Item
                  title="Wallet Balance"
                  value={
                    <span>
                      <span className="currency">NGN</span>
                      <NumberFormat
                        thousandSeparator={true}
                        prefix=""
                        className="numeric"
                        displayType="text"
                        value={Number(history.new_wallet_balance).toFixed(2)}
                      />
                    </span>
                  }
                />
              )}
            </div>
          )}
        </div>
      ))
    ) : (
      <Empty description="No history available" />
    )}
    {totalItems > pageSize && (
      <div
        className="pagination-controls mb-10"
        style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}
      >
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
          style={{
            marginRight: "10px",
            padding: "5px 10px",
            backgroundColor: currentPage === 1 ? "#ccc" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          Previous
        </button>
        <span style={{ alignSelf: "center", margin: "0 10px" }}>
          Page {currentPage} of {Math.ceil(totalItems / pageSize)}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= Math.ceil(totalItems / pageSize)}
          className="pagination-btn"
          style={{
            marginLeft: "10px",
            padding: "5px 10px",
            backgroundColor:
              currentPage >= Math.ceil(totalItems / pageSize)
                ? "#ccc"
                : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              currentPage >= Math.ceil(totalItems / pageSize)
                ? "not-allowed"
                : "pointer",
          }}
        >
          Next
        </button>
      </div>
    )}
  </div>
);

const FourwardHistoryList = ({ data }) => {
  if (!data || data.length === 0)
    return <Empty description="No 4Ward history available" />;

  const formatFourwardDate = (dateVal) => {
    if (!dateVal) return null;
    const str = String(dateVal);
    const dt = str.includes("T")
      ? DateTime.fromISO(str)
      : DateTime.fromJSDate(new Date(str));
    return dt.isValid ? dt.toFormat("d MMMM yyyy") : null;
  };

  const items = data.map((history, index) => {
    const amount = Number(
      history.amount || history.apvAmount || history.depositAmount || 0,
    );
    const transactionType = history.type || "DEBIT";

    const typeLabel =
      transactionType === "DEPOSIT"
        ? "4Ward deposit"
        : transactionType === "APV_CREDIT"
          ? "4Ward credit"
          : "4Ward debit";

    const rawDate =
      history.maturityDate || history.createdAt || history.timestamp;
    const formattedDate = formatFourwardDate(rawDate);
    const isMaturity =
      transactionType === "DEPOSIT" ||
      transactionType === "APV_CREDIT" ||
      !!history.maturityDate;
    const dateLabel = isMaturity ? "Maturity date" : "Date";

    const formattedAmount = `NGN ${amount.toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    const headerTitle = formattedDate
      ? `${formattedAmount} ${typeLabel} - ${dateLabel}: ${formattedDate}`
      : `${formattedAmount} ${typeLabel}`;

    return buildAdminCollapseItem({
      key: history.id || `fourward-${index}`,
      headerTitle,
      headerTagText: history.status || "—",
      headerTagColor: history.status === "COMPLETED" ? "green" : "orange",
      items: [
        { label: "Type", value: transactionType },
        { label: "Amount", value: formattedAmount },
        {
          label: "Deposit",
          value: `NGN ${Number(history.depositAmount || 0).toLocaleString(
            "en-NG",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          )}`,
        },
        {
          label: "APV",
          value: `NGN ${Number(history.apvAmount || 0).toLocaleString("en-NG", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        },
        {
          label: "Maturity",
          value: formattedDate || "—",
        },
        {
          label: "Description",
          value: history.description || "—",
          fullWidth: true,
        },
      ],
    });
  });

  return <AdminCollapseList items={items} />;
};

const Item = ({ title, value }) => (
  <div className="detail-row">
    <div className="detail-label">{title}</div>
    <div className="detail-value">{value}</div>
  </div>
);
